import type { SiteCmsDocumentResponse, SiteCmsMenuItem, SiteCmsPageDraft } from '@shared/types/entities';

import { cmsKeepIdentity } from './site-cms-workspace-model';

const RESERVED = new Set(['assets', 'documents', 'healthz', 'robots', 'sitemap']);

/** Local convenience only: backend enforces canonical slug constraints. */
export function nextCmsPageSlug(title: string, pages: SiteCmsPageDraft[]): string {
  const slug = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 54)
    .replace(/-+$/g, '');
  const base = slug && !RESERVED.has(slug) ? slug : 'page';
  const taken = new Set(pages.map(page => page.slug));
  for (let n = 1; n < 1000; n++) {
    const candidate = n === 1 ? base : `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  throw new Error('Trop de pages avec le même titre');
}

export function menuUsesPage(menu: SiteCmsMenuItem[], slug: string): boolean {
  return menu.some(item => (item.type === 'PAGE' && item.slug === slug) || menuUsesPage(item.children, slug));
}

export function menuUsesDocument(menu: SiteCmsMenuItem[], slug: string): boolean {
  return menu.some(item => (item.type === 'DOCUMENT' && item.slug === slug) || menuUsesDocument(item.children, slug));
}

export function nextCmsDocumentSlug(title: string, documents: SiteCmsDocumentResponse[]): string {
  const base =
    title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 54)
      .replace(/-+$/g, '') || 'document';
  const safe = RESERVED.has(base) ? 'document' : base;
  const existing = new Set(documents.map(doc => doc.slug));
  for (let n = 1; n < 1000; n++) {
    const slug = n === 1 ? safe : `${safe}-${n}`;
    if (!existing.has(slug)) return slug;
  }
  throw new Error('Nombre maximal de documents atteint');
}

/** Update every internal reference when editing a page address; no stale menu IDs. */
export function renameCmsPage(
  draft: import('@shared/types/entities').SiteCmsDraftResponse,
  oldSlug: string,
  nextSlug: string,
): import('@shared/types/entities').SiteCmsDraftResponse {
  if (
    oldSlug === 'home' ||
    !/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/.test(nextSlug) ||
    RESERVED.has(nextSlug) ||
    nextSlug === 'home'
  )
    throw new Error('Adresse invalide : utilisez des minuscules, chiffres et tirets.');
  if (draft.pages.some(p => p.slug === nextSlug && p.slug !== oldSlug))
    throw new Error('Cette adresse est déjà utilisée.');
  const rewrite = (href: string) => {
    const split = href.search(/[?#]/);
    const clean = split < 0 ? href : href.slice(0, split);
    const suffix = split < 0 ? '' : href.slice(split);
    return clean === `/${oldSlug}` || clean === `/${oldSlug}/` ? `/${nextSlug}/${suffix}` : href;
  };
  const rich = (
    node: import('@shared/types/entities').SiteCmsRichNode,
  ): import('@shared/types/entities').SiteCmsRichNode => ({
    ...node,
    ...(node.marks
      ? {
          marks: node.marks.map(m =>
            m.type === 'link' && m.attrs ? { ...m, attrs: { href: rewrite(m.attrs.href) } } : m,
          ),
        }
      : {}),
    ...(node.content ? { content: node.content.map(rich) } : {}),
  });
  const menu = (items: SiteCmsMenuItem[]): SiteCmsMenuItem[] =>
    items.map(item =>
      cmsKeepIdentity(item, {
        ...item,
        ...(item.type === 'PAGE' && item.slug === oldSlug ? { slug: nextSlug } : {}),
        ...(item.type === 'EXTERNAL_LINK' ? { url: rewrite(item.url) } : {}),
        children: menu(item.children),
      }),
    );
  return {
    ...draft,
    pages: draft.pages.map(p => ({
      ...p,
      slug: p.slug === oldSlug ? nextSlug : p.slug,
      blocks: p.blocks.map(block => {
        if (block.type === 'button' || block.type === 'cover' || block.type === 'cta')
          return cmsKeepIdentity(block, { ...block, href: rewrite(block.href) });
        if (block.type === 'text' || block.type === 'imageText')
          return cmsKeepIdentity(block, {
            ...block,
            ...(block.rich
              ? { rich: rich(block.rich) }
              : {
                  text: block.text.replace(
                    /\[([^\]]+)\]\(([^\s)]+)\)/g,
                    (_, title: string, href: string) => `[${title}](${rewrite(href)})`,
                  ),
                }),
          });
        if (block.type === 'partners')
          return cmsKeepIdentity(block, {
            ...block,
            items: block.items.map(item => ({ ...item, url: rewrite(item.url) })),
          });
        return block;
      }),
    })),
    headerMenu: menu(draft.headerMenu),
  };
}
